import { ExpandMore } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { MapPinIcon } from "assets/styles/icons";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
  AppSecondaryButton,
} from "shared/components/AppComponents";
import { MORGAGE_OPTIONS, PROPERTY_TYPE } from "shared/helpers/Constants";
import { NewPropertiesSchema } from "shared/helpers/Schema";
import { handleSaveProperty } from "../../security/LiabilitiesLegalTies";
import { ProfileActions } from "state/profile/Action";
import { useProgress } from "context/ProgressProvider";

const DialogAddProperty = ({
  showDialogAddProperty,
  setShowDialogAddProperty,
  numberNewProperties,
  setShowRemoveCaveats,
  handleOpenMortgage,
}: {
  showDialogAddProperty: boolean;
  setShowDialogAddProperty: any;
  numberNewProperties: number;
  setShowRemoveCaveats: any;
  handleOpenMortgage: any;
}) => {
  const [multiPropertiesData, setMultiPropertiesData] = useState([]);
  const [submittedProperties, setSubmittedProperties] = useState([]);
  const [currentEstimatedAmount, setCurrentEstimatedAmount] = useState("0");
  const { currentProfile } = useProgress();
  const dispatch = useDispatch();
  const actualNewProperties =
    numberNewProperties - currentProfile.noOfProperties;

  const currentPropertyIndex = multiPropertiesData.findIndex(
    (item) => item.status === "pending"
  );

  const handleSubmitForm = (formValues, formik) => {
    if (formValues.isAnyCaveats) {
      setShowRemoveCaveats(true);
      formik.setSubmitting(false);
      return;
    }
    setCurrentEstimatedAmount("0");
    console.log(formValues);
    const updatedValues = {
      ...formValues,
      existingLoanSettlementOption: formValues.isAnyExistingLoan
        ? formValues.existingLoanSettlementOption
        : "",
      existingLoanAmount: formValues.isAnyExistingLoan
        ? parseFloat(String(formValues.existingLoanAmount).replace(/,/g, ""))
        : "",
      estimatedPropertyAmount: parseFloat(
        String(formValues.estimatedPropertyAmount).replace(/,/g, "")
      ),
    };

    const updatedSubmittedProperties = [...submittedProperties, updatedValues];
    setSubmittedProperties(updatedSubmittedProperties);

    if (currentPropertyIndex < actualNewProperties - 1) {
      const updatedData = [...multiPropertiesData];
      updatedData[currentPropertyIndex] = {
        ...updatedValues,
        status: "completed",
      };
      setMultiPropertiesData(updatedData);

      setTimeout(() => {
        formik.resetForm();
        formik.setTouched({});
      }, 0);
    } else {
      handleSaveProperty(
        true,
        {
          isPropertyOwned: true,
          noOfProperties: numberNewProperties,
          properties: [
            ...currentProfile.properties.map(
              ({ isAllDataFilled, _id, ...rest }) => rest
            ),
            ...updatedSubmittedProperties.map(({ status, ...rest }) => rest),
          ],
        },
        currentProfile.loadId,
        (res) => {
          if (res) {
            dispatch(
              ProfileActions.setSblProfile({
                noOfProperties: numberNewProperties,
                properties: [
                  ...currentProfile.properties,
                  ...updatedSubmittedProperties.map(
                    ({ status, ...rest }) => rest
                  ),
                ],
              })
            );
            setShowDialogAddProperty(false);
          } else {
            formik.setSubmitting(false);
          }
        }
      );
    }
  };

  let baseProperty = {
    estimatedPropertyAmount: null,
    existingLoanAmount: "",
    existingLoanSettlementOption: "",
    isAnyCaveats: "",
    isAnyExistingLoan: "",
    propertyAddress: "",
    propertyType: "",
    status: "pending",
  };

  useEffect(() => {
    const dataProperties = Array.from({ length: actualNewProperties }, () => ({
      ...baseProperty,
    }));

    setMultiPropertiesData(dataProperties);
    setSubmittedProperties([]);
  }, [numberNewProperties]);

  useEffect(() => {
    setMultiPropertiesData([]);
  }, []);

  return (
    <Dialog
      open={showDialogAddProperty}
      fullWidth
      maxWidth="sm"
      onClose={() => setShowDialogAddProperty(false)}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h2">Add New Property</Typography>
        </Box>
      </DialogTitle>

      <Formik
        enableReinitialize
        validationSchema={NewPropertiesSchema(currentEstimatedAmount)}
        validateOnMount
        onSubmit={(formValues, formik) => {
          handleSubmitForm(formValues, formik);
        }}
        initialValues={{
          estimatedPropertyAmount: "",
          existingLoanAmount: "",
          existingLoanSettlementOption: "",
          isAnyCaveats: "",
          isAnyExistingLoan: "",
          propertyAddress: "",
          propertyType: "",
        }}
      >
        {({
          handleSubmit,
          errors,
          isSubmitting,
          values,
          setValues,
          dirty,
          setTouched,
        }: any) => {
          return (
            <>
              <DialogContent>
                <Typography variant="h3" fontWeight={400} mb={"1.25rem"}>
                  New property {currentPropertyIndex + 1} of{" "}
                  {actualNewProperties}
                </Typography>
                <Box mt={2}>
                  <AppFormikSelect
                    placeholder="Select"
                    label="Property type"
                    name="propertyType"
                    items={PROPERTY_TYPE}
                    valueKey="value"
                    titleKey="label"
                    outerLabel
                    iconComponent={ExpandMore}
                    className="ezifin-default-field"
                    labelSize={16}
                  />
                </Box>
                <Box mt={2}>
                  <AppFormikTextField
                    placeholder="Address"
                    label="Property address"
                    name="propertyAddress"
                    outerLabel
                    className="ezifin-default-field"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <MapPinIcon />
                          </InputAdornment>
                        ),
                      },
                    }}
                    allowComma={true}
                  />
                </Box>
                <Box mt={2}>
                  <AppFormikTextField
                    placeholder="Value"
                    label="Estimated property amount"
                    name="estimatedPropertyAmount"
                    outerLabel
                    className="ezifin-default-field"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                    allowComma={true}
                    customIsoDollarDecimal
                    onChange={(e) => {
                      console.log(e.target.value);
                      setCurrentEstimatedAmount(e.target.value);
                    }}
                  />
                </Box>
                <Box mt={2}>
                  <AppFormikSelect
                    placeholder="Select"
                    label="Any existing loan"
                    name="isAnyExistingLoan"
                    items={[
                      {
                        value: true,
                        label: "Yes",
                      },
                      {
                        value: false,
                        label: "No",
                      },
                    ]}
                    valueKey="value"
                    titleKey="label"
                    outerLabel
                    iconComponent={ExpandMore}
                    className="ezifin-default-field"
                    labelSize={16}
                  />
                </Box>
                {values.isAnyExistingLoan && (
                  <>
                    <Box mt={2}>
                      <AppFormikSelect
                        placeholder="Select"
                        label="Existing loan settlement"
                        name="existingLoanSettlementOption"
                        items={MORGAGE_OPTIONS}
                        valueKey="value"
                        titleKey="label"
                        outerLabel
                        iconComponent={ExpandMore}
                        className="ezifin-default-field"
                        labelSize={16}
                        onChange={(e) => {
                          if (e.target.value === "I’ll decide later") {
                            handleOpenMortgage();
                          }
                        }}
                      />
                    </Box>
                    <Box mt={2}>
                      <AppFormikTextField
                        placeholder="Enter amount"
                        label="Existing loan amount"
                        name="existingLoanAmount"
                        outerLabel
                        className="ezifin-default-field"
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          },
                        }}
                        customIsoDollar
                      />
                    </Box>
                  </>
                )}

                <Box mt={2}>
                  <AppFormikSelect
                    placeholder="Select"
                    label="Any caveats"
                    name="isAnyCaveats"
                    items={[
                      {
                        value: true,
                        label: "Yes",
                      },
                      {
                        value: false,
                        label: "No",
                      },
                    ]}
                    valueKey="value"
                    titleKey="label"
                    outerLabel
                    iconComponent={ExpandMore}
                    className="ezifin-default-field"
                    labelSize={16}
                    onChange={(e) => {
                      if (e.target.value) {
                        setShowRemoveCaveats(true);
                        setTouched({});
                      }
                    }}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Box
                  sx={{
                    display: "flex",
                    gap: "12px",
                    width: "100%",
                    paddingTop: "12px",
                  }}
                >
                  <AppSecondaryButton
                    title="Cancel"
                    className="btn-small"
                    onClick={() => setShowDialogAddProperty(false)}
                    fullWidth
                  />
                  <AppPrimaryButton
                    title={
                      currentPropertyIndex < actualNewProperties - 1
                        ? "Next"
                        : "Submit"
                    }
                    isLoading={isSubmitting}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={
                      !dirty || Object.keys(errors).length > 0 || isSubmitting
                    }
                    fullWidth
                  />
                </Box>
              </DialogActions>
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default DialogAddProperty;
